import { useEffect, useState } from "react";
import { CanvasDrawer } from "../utils/drawer.js";
import { JamDrawerProps } from "../types.js";
import { clearCanvas, drawCanvasFromBackground } from "./controls.js";

export function useDrawer<T extends JamDrawerProps>(
  args: Partial<T> & { disabled?: boolean }
) {
  const {
    tilesProps,
    canvasStatus,
    canvasDrawer,
    drawersObservable,
    paramsObserver,
    drawerIndex,
    controls,
    disabled,
    ...others
  } = args;

  const [drawer, setDrawer] = useState(new CanvasDrawer());

  useEffect(() => {
    canvasDrawer && setDrawer(canvasDrawer);
  }, [canvasDrawer]);

  useEffect(() => {
    if (disabled) return;
    if (!drawersObservable) return;
    if (typeof drawerIndex !== "number") return;
    if (!controls) return;

    const drawers = drawersObservable.getValue("drawers");
    if (drawers.length <= drawerIndex) drawers[drawerIndex] = drawer;
    else drawers.splice(drawerIndex, 0, drawer);
    drawersObservable.setValue("drawers", drawers);

    return () => {
      const drawers = drawersObservable
        .getValue("drawers")
        .filter((item) => item !== drawer);

      drawersObservable.setValue("drawers", drawers);

      drawer.currentStatus &&
        drawers.forEach((item) => item.observable.trigger());
    };
  }, [disabled, drawersObservable, drawer, controls, drawerIndex]);

  useEffect(() => {
    if (disabled) return;
    if (!drawersObservable) return;
    if (!controls) return;
    if (!canvasStatus?.bgColor) return;

    const { bgColor } = canvasStatus;

    const listener = drawer.observable.subscribe(({ status }) => {
      return requestAnimationFrame(() => {
        if (!status) return;
        const transformation = controls.transformation();
        if (!transformation) return;
        clearCanvas({ canvas: controls.element, bgColor });
        const drawers = drawersObservable.getValue("drawers");
        for (const canvasDrawer of drawers) {
          if (!canvasDrawer?.currentStatus) continue;
          drawCanvasFromBackground({
            canvas: controls.element,
            canvasDrawer,
            transformation,
          });
        }
        paramsObserver?.sendDraw(status);
      });
    });

    return () => {
      listener();
    };
  }, [
    disabled,
    drawersObservable,
    paramsObserver,
    controls,
    drawer,
    canvasStatus?.bgColor,
  ]);

  if (
    !disabled &&
    tilesProps &&
    canvasStatus &&
    drawersObservable &&
    typeof drawerIndex === "number" &&
    controls
  )
    return {
      ...others,
      tilesProps,
      canvasStatus,
      drawersObservable,
      drawerIndex,
      controls,
      canvasDrawer: drawer,
    };
  return;
}
